/**
 * The PDP's offer button ("Wir gestalten, Sie erhalten ein Angebot").
 *
 * It asks the `<main-product>` controller, the single source of truth for variant,
 * quantity and size split, which lines to quote — one per size when the product has a
 * size picker — then pushes them onto the quote basket and forwards to the quote page.
 * Nothing touches the Shopify cart.
 *
 * The controller resolves each line's price breaks and minimum, so the quote page can
 * price it without another network call.
 *
 * A classic script, not an ES module: relative `import` does not resolve against
 * Shopify's hashed CDN asset urls. `OfferList` is taken from the global that
 * `offer-list.js` publishes; it is loaded before this script.
 */
const OfferList = window.OfferList;

if (!customElements.get('offer-button')) {
  customElements.define(
    'offer-button',
    class OfferButton extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.button?.addEventListener('click', this.onClick);
      }

      get controller() {
        return this.closest('main-product');
      }

      onClick = (event) => {
        event.preventDefault();

        if (this.button?.disabled) return;

        // The controller disables the button for anything it cannot quote, but a stale
        // click (or a missing controller) must not be able to send an empty request.
        const selections = this.controller?.offerSelections?.() ?? [];
        if (!selections.length) {
          console.error('The offer button could not resolve the selected configuration');
          return;
        }

        const product = this.dataset;
        const image = this.variantImage();

        const lines = selections.map(({ variant, quantity, min, priceBreaks, group, groupMin }) => ({
          variantId: variant.id,
          productId: Number(product.productId),
          handle: product.productHandle,
          title: product.productTitle,
          variantTitle: this.variantTitle(variant),
          sku: variant.sku ?? '',
          image,
          url: `${product.productUrl}?variant=${variant.id}`,
          price: variant.price,
          quantity,
          min,
          priceBreaks,
          ...(group ? { group, groupMin } : {}),
        }));

        OfferList.addMany(lines);
        window.location.href = this.dataset.offerUrl;
      };

      /**
       * Option values joined for display, e.g. "Navy | M".
       *
       * A product with no real variants has a single synthetic option, "Default Title";
       * showing it would print a meaningless label under every such line, so it collapses
       * to an empty string — the same case the cart drawer skips with
       * `has_only_default_variant`.
       */
      variantTitle(variant) {
        const options = variant.options ?? [];
        if (options.length === 1 && options[0] === 'Default Title') return '';
        return options.join(' | ');
      }

      /**
       * Prefers the image currently shown in the gallery, which the controller has already
       * filtered to the selected colour, so the quote card shows the variant the customer
       * actually configured rather than the product's default.
       */
      variantImage() {
        const active = this.controller?.querySelector('.main-product__slide.swiper-slide-active img');
        const source = active?.currentSrc || active?.src || this.dataset.productImage || '';

        if (!source) return '';

        // Ask the CDN for the size the quote card renders at, not the gallery's.
        return source.replace(/width=\d+/, 'width=300');
      }
    }
  );
}
